import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SoundTherapyStep } from './SoundTherapyStep';
import { BreathingStep } from './BreathingStep';
import { BeliefDetoxStep } from './BeliefDetoxStep';
import { ProfilingQuestion, hasMoreQuestions } from './ProfilingQuestion';
import { SomaticTracking } from '../SomaticTracking';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useClickSound } from '../../hooks/useClickSound';
import { TherapyConfig } from '../EmotionScanner';
import { useTTS } from '../../hooks/useTTS';
import { TTS_TEXTS } from '../../config/prompts';
const SOMATIC_VARIANTS = ['infinity', 'circles', 'wave', 'triangle', 'square', 'spiral'] as const;
interface DailyRitualProps {
  onComplete: (pointsEarned: number) => void;
  onClose?: () => void;
  therapyConfig?: TherapyConfig;
}
export function DailyRitual({
  onComplete,
  onClose,
  therapyConfig
}: DailyRitualProps) {
  const [step, setStep] = useState(1);
  const {
    playSuccess,
    playClick
  } = useClickSound();
  const {
    speak,
    stop: stopTTS
  } = useTTS();
  // Decide at mount: 50% chance somatic step appears, pick random variant
  const somaticConfig = useMemo(() => {
    const showSomatic = Math.random() < 0.5;
    const variant = SOMATIC_VARIANTS[Math.floor(Math.random() * SOMATIC_VARIANTS.length)];
    return {
      showSomatic,
      variant
    };
  }, []);
  // Use therapy config or defaults
  const soundHz = therapyConfig?.sound_hz ?? 432;
  const soundName = therapyConfig?.sound_name ?? '432 Hz — Częstotliwość Ziemi';
  const ritualDescription = therapyConfig?.ritual_description ?? 'Terapia dźwiękiem przywracająca naturalny przepływ energii.';
  const breathingTechnique = therapyConfig?.breathing_technique ?? 'box';
  const breathingName = therapyConfig?.breathing_name ?? 'Oddech Pudełkowy';
  const breathingPattern = therapyConfig?.breathing_pattern ?? [4, 4, 4, 4];
  const ritualTitle = therapyConfig?.ritual_title ?? 'Rytuał Harmonii';
  // Step flow:
  // 1: Sound → 2: Breathing → 3: Somatic (if enabled, else skip) → 4: BeliefDetox → 5: Summary → 6: Profiling
  const handleStepComplete = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (somaticConfig.showSomatic) {
        setStep(3); // Somatic
      } else {
        setStep(4); // Skip to BeliefDetox
      }
    } else if (step === 3) {
      setStep(4); // Somatic → BeliefDetox
    } else if (step === 4) {
      setStep(5); // BeliefDetox → Summary
      playSuccess();
      speak(TTS_TEXTS.ritual_complete);
    }
  };

  // Clean up TTS on unmount
  React.useEffect(() => {
    return () => {
      stopTTS();
    };
  }, []);
  const handleSummaryNext = () => {
    playClick();
    if (hasMoreQuestions()) {
      setStep(6); // Profiling question
    } else {
      onComplete(10);
    }
  };
  const handleFinish = () => {
    playClick();
    onComplete(10);
  };
  // Map sound_hz to a color
  const getSoundColor = () => {
    if (soundHz <= 432) return '16, 185, 129'; // emerald
    if (soundHz <= 528) return '99, 102, 241'; // indigo
    if (soundHz <= 852) return '168, 85, 247'; // purple
    return '14, 165, 233'; // cyan
  };
  return <AnimatePresence mode="wait" data-id="element-1019">
      {step === 1 && <SoundTherapyStep key="step1" frequency={soundHz} colorRGB={getSoundColor()} duration={30} onComplete={handleStepComplete} onClose={onClose} soundName={soundName} soundDesc={ritualDescription} data-id="element-1020" />}
      {step === 2 && <BreathingStep key="step2" onComplete={handleStepComplete} onClose={onClose} breathingTechnique={breathingTechnique} breathingName={breathingName} breathingPattern={breathingPattern} data-id="element-1021" />}
      {step === 3 && somaticConfig.showSomatic && <SomaticTracking key="step3-somatic" onComplete={handleStepComplete} onClose={onClose} isStandalone={false} initialVariant={somaticConfig.variant} data-id="element-1022" />}
      {step === 4 && <BeliefDetoxStep key="step4" onComplete={handleStepComplete} onClose={onClose} data-id="element-1023" />}
      {step === 5 && <motion.div key="summary" className="fixed inset-0 z-50 flex flex-col items-center justify-center w-full h-full bg-zen-bg text-zen-text p-6" initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0
    }} data-id="element-1024">
          <div className="w-24 h-24 bg-yellow-50 rounded-full flex items-center justify-center mb-6 border-4 border-yellow-100 shadow-xl" data-id="element-1025">
            <Sparkles className="w-10 h-10 text-yellow-500" fill="currentColor" data-id="element-1026" />
          </div>
          <h2 className="text-3xl font-light mb-2 text-center" data-id="element-1027">
            {ritualTitle} Zakończony
          </h2>
          <p className="text-slate-500 text-center mb-8 max-w-xs" data-id="element-1028">
            Twoje ciało, energia i umysł są teraz w pełnej harmonii.
          </p>

          <div className="flex space-x-4 mb-12" data-id="element-1029">
            <div className="clean-panel rounded-2xl p-4 flex flex-col items-center min-w-[100px]" data-id="element-1030">
              <span className="text-xs text-slate-400 uppercase tracking-widest mb-1" data-id="element-1031">
                Energia
              </span>
              <span className="text-xl font-bold text-yellow-600 flex items-center" data-id="element-1032">
                +10 <Sparkles className="w-4 h-4 ml-1" fill="currentColor" data-id="element-1033" />
              </span>
            </div>
            <div className="clean-panel rounded-2xl p-4 flex flex-col items-center min-w-[100px]" data-id="element-1034">
              <span className="text-xs text-slate-400 uppercase tracking-widest mb-1" data-id="element-1035">
                Dni z rzędu
              </span>
              <span className="text-xl font-bold text-zen-accent flex items-center" data-id="element-1036">
                3 🔥
              </span>
            </div>
          </div>

          <button onClick={handleSummaryNext} className="w-full max-w-xs bg-zen-accent text-white rounded-full py-4 text-sm font-bold hover:bg-zen-accent/90 transition-colors shadow-lg flex items-center justify-center space-x-2" data-id="element-1037">
            <span data-id="element-1038">{hasMoreQuestions() ? 'Dalej' : 'Wróć do kokpitu'}</span>
            <ArrowRight className="w-4 h-4" data-id="element-1039" />
          </button>
        </motion.div>}
      {step === 6 && <ProfilingQuestion key="profiling" onComplete={handleFinish} data-id="element-1040" />}
    </AnimatePresence>;
}